import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, IconButton, Grid, Paper, TextField, Button,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Snackbar,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as Icons from '@material-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import DataTable from '../components/DataTable';
import { invokeApig } from '../lib/awsLib';
import NeokodeButton from '../components/NeokodeButton';

const useStyles = makeStyles((theme) => ({
  section: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  paper: {
    padding: theme.spacing(2),
  },
  gridMaxMd: {
    [theme.breakpoints.up('md')]: {
      maxWidth: theme.breakpoints.values.lg,
    },
  },
  deleteButton: {
    backgroundColor: '#f44336',
    '&:hover': { backgroundColor: '#f44336' },
  },
  resetButton: {
    backgroundColor: '#2196f3',
    '&:hover': { backgroundColor: '#2196f3' },
  },
  editIcon: {
    color: '#2196f3',
  },
  lockIcon: {
    color: '#ff9800',
  },
  deleteIcon: {
    color: '#f44336',
  },
  toolbar: {
    padding: theme.spacing(0.5, 0.5, 0),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
  searchField: {
    [theme.breakpoints.down('xs')]: { width: '100%' },
    margin: theme.spacing(1, 0.5, 1.5),
    '& .MuiSvgIcon-root': { marginRight: theme.spacing(0.5) },
    '& .MuiInput-underline:before': { borderBottom: `1px solid ${theme.palette.divider}` },
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  refreshIcon: {
    color: '#666',
  },
  buttonsDialogContainer: {
    padding: '10px 25px',
  },
  button: {
    margin: '15px',
  },
}));

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function UsersPage(props) {
  const classes = useStyles();
  const [isGlobalError, setIsGlobalError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [username, setUsername] = useState('');
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [isDeleteUser, setIsDeleteUser] = useState(false);
  const [message, setMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [messageToast, setMessageToast] = useState('');

  useEffect(() => {
    getUsers();
  }, []);

  const getUsers = async () => {
    try {
      setIsLoading(true);
      setIsGlobalError(false);
      setUsers([]);
      setRows([]);
      setSearchText('');
      const response = await invokeApig({
        path: '/user/find',
        method: 'POST',
        body: { page: 0, size: 100 },
      });
      if (response.code === 0) {
        setUsers(response.results);
        setRows(response.results);
      } else {
        setMessage(
          response.code === 403
            ? response.message
            : 'Error al obtener los usuarios. Intenta nuevamente o comunícate con Soporte'
        );
        setIsGlobalError(true);
      }
    } catch (error) {
      setMessage('Error al obtener los usuarios. Intenta nuevamente o comunícate con Soporte');
      setIsGlobalError(true);
    }
    setIsLoading(false);
  };

  const resetPassword = async (username) => {
    try {
      setIsLoading(true);
      const response = await invokeApig({
        path: '/user/reset',
        method: 'POST',
        body: { username },
      });
      if (!response || response.errorMessage) {
        setMessage(response?.errorMessage || 'Se ha producido un error al resetear la contraseña');
        setIsError(true);
      } else {
        setMessageToast('Se ha enviado un correo al usuario con las instrucciones para cambiar la contraseña');
        setOpenToast(true);
        setOpenDialog(false);
      }
    } catch (error) {
      setMessage('Ocurrió un error al resetear la clave. Por favor intenta más tarde o comunícate con Soporte');
      setIsError(true);
    }
    setIsLoading(false);
  };

  const deleteUser = async (username) => {
    try {
      setIsLoading(true);
      const response = await invokeApig({
        path: `/user/${username}`,
        method: 'DELETE',
      });
      if (response.code !== 0 || response.errorMessage) {
        setMessage(
          response.code === 403
            ? 'No tienes permiso para eliminar usuarios'
            : response.message || response.errorMessage || 'Error al eliminar el usuario'
        );
        setIsError(true);
      } else {
        setMessageToast('Se ha eliminado el usuario correctamente');
        setOpenToast(true);
        setOpenDialog(false);
      }
    } catch (error) {
      setMessage('Ocurrió un error al eliminar el usuario. Por favor intenta más tarde o comunícate con Soporte');
      setIsError(true);
    }
    setIsLoading(false);
  };

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = users.filter((row) =>
      Object.keys(row).some((field) => searchRegex.test(row[field].toString()))
    );
    setRows(filteredRows);
  };

  const handleShowResetPassword = (username) => {
    setUsername(username);
    setIsResetPassword(true);
    setIsDeleteUser(false);
    setIsError(false);
    setOpenDialog(true);
  };

  const handleShowDelete = (username) => {
    setUsername(username);
    setIsResetPassword(false);
    setIsDeleteUser(true);
    setIsError(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleConfirmAction = () => {
    if (isResetPassword) resetPassword(username);
    if (isDeleteUser) deleteUser(username);
  };

  const columns = [
    { id: 'name', label: 'Nombre' },
    { id: 'lastName', label: 'Apellido' },
    { id: 'email', label: 'Email', ellipsis: true },
    { id: 'status', label: 'Estado', sortable: false },
    {
      id: 'actions',
      label: 'Acciones',
      sortable: false,
      render: (row) => (
        <>
          <IconButton
            size="small"
            component={RouterLink}
            to={`/users/edit/${row.username}`}
            className={classes.editIcon}
          >
            <Icons.Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleShowResetPassword(row.username)}
            className={classes.lockIcon}
          >
            <Icons.Lock fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleShowDelete(row.username)}
            className={classes.deleteIcon}
          >
            <Icons.Delete fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const renderError = () => (
    <>
      <Alert severity="error">{message}</Alert>
      <NeokodeButton
        type="button"
        variant="contained"
        className={classes.button}
        component={RouterLink}
        to="/"
      >
        Volver al inicio
      </NeokodeButton>
    </>
  );

  return (
    <Grid container spacing={2} className={classes.section} justifyContent="center">
      <Grid item xs={12} sm={12} md={12} lg={12} className={classes.gridMaxMd}>
        <Paper className={classes.paper}>
          <Typography variant="h4" gutterBottom>
            Administración de Usuarios
          </Typography>

          {isGlobalError ? (
            renderError()
          ) : (
            <>
              <div className={classes.toolbar}>
                <TextField
                  variant="standard"
                  value={searchText}
                  onChange={(e) => requestSearch(e.target.value)}
                  placeholder="Buscar…"
                  className={classes.searchField}
                  InputProps={{
                    startAdornment: <Icons.Search fontSize="small" />,
                    endAdornment: (
                      <IconButton
                        title="Limpiar"
                        size="small"
                        style={{ visibility: searchText ? 'visible' : 'hidden' }}
                        onClick={() => requestSearch('')}
                      >
                        <Icons.Clear fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
                <div className={classes.toolbarActions}>
                  <IconButton
                    className={classes.refreshIcon}
                    onClick={getUsers}
                    disabled={isLoading}
                  >
                    <Icons.Refresh />
                  </IconButton>
                  <NeokodeButton
                    variant="contained"
                    startIcon={<Icons.Add />}
                    component={RouterLink}
                    disabled={isLoading}
                    to="/users/create"
                  >
                    Agregar Usuario
                  </NeokodeButton>
                </div>
              </div>

              <DataTable
                columns={columns}
                rows={rows}
                getRowId={(row) => row.username}
                loading={isLoading}
                emptyMessage="No hay usuarios para mostrar."
                emptyIcon={Icons.PeopleOutlined}
                initialPageSize={10}
              />

              <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Confirmación</DialogTitle>
                <DialogContent>
                  <Alert severity="info">
                    {isResetPassword && (
                      <p>Se va a resetear la contraseña. Le llegará un correo electrónico con una contraseña temporal al usuario.</p>
                    )}
                    {isDeleteUser && (
                      <p>Se va a eliminar el usuario y ya no tendrá acceso a la plataforma.</p>
                    )}
                    <p><b>¿Estás seguro que quieres continuar con la acción?</b></p>
                  </Alert>
                  {isError && <Alert severity="error" style={{ marginTop: 8 }}>{message}</Alert>}
                </DialogContent>
                <DialogActions className={classes.buttonsDialogContainer}>
                  <Button
                    variant="contained"
                    className={classes.button}
                    onClick={handleCloseDialog}
                    disabled={isLoading}
                  >
                    No, cerrar esta ventana
                  </Button>
                  <Button
                    variant="contained"
                    className={isDeleteUser ? classes.deleteButton : classes.resetButton}
                    onClick={handleConfirmAction}
                    disabled={isLoading}
                  >
                    Sí, confirmo
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Paper>
      </Grid>

      <Snackbar open={openToast} autoHideDuration={6000} onClose={() => setOpenToast(false)}>
        <Alert onClose={() => setOpenToast(false)} severity="success">
          {messageToast}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default UsersPage;
